
import { Users, Activity, RefreshCw, CheckCircle, Brain } from 'lucide-react';
import { PhaseLayout } from "./PhaseLayout";

export const PhaseThree = () => {
  return (
    <PhaseLayout 
      order={3}
      icon={<Users className="text-white w-8 h-8" />}
      title="Phase 3: Dynamic Scoring & Collaboration"
      description="Wamy continuously monitors claims, dynamically updates risk scores, and facilitates seamless collaboration among all stakeholders."
      steps={[
        {
          number: "1",
          title: "Continuous Data Monitoring",
          description: "Detects and processes new claim information in real time"
        },
        {
          number: "2",
          title: "Dynamic Risk Scoring",
          description: "Adjusts risk scores based on evolving data and insights"
        },
        {
          number: "3",
          title: "Intelligent Collaboration",
          description: "Streamlines communication and decision-making, ensuring all stakeholders stay aligned"
        }
      ]}
      contentRight={<CollaborationIllustration />}
    />
  );
};

const CollaborationIllustration = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 transform transition-transform hover:scale-105">
      {/* Dashboard Header with Claim Status */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-medium text-gray-800">Claim #JPC-2023</h4>
          <p className="text-sm text-gray-500">Johnson Property</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-2.5 py-1 bg-blue-100 rounded-full">
            <span className="text-xs font-medium text-blue-800">Live Monitoring</span>
          </div>
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
            <Activity className="w-4 h-4 text-blue-600" />
          </div>
        </div>
      </div>
      
      {/* Dynamic Risk Score */}
      <div className="mb-6 bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-sm font-semibold text-gray-700">Risk Score Evolution</h5>
          <div className="flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5 text-teal-600" />
            <span className="text-xs text-teal-600">Auto-updating</span>
          </div>
        </div>
        
        <div className="flex items-end gap-1 h-12 mb-1">
          <div className="w-1/6 bg-orange-200 rounded-sm" style={{ height: '40%' }}>
            <div className="h-full flex items-end justify-center pb-0.5">
              <span className="text-[10px] text-orange-800">52</span>
            </div>
          </div>
          <div className="w-1/6 bg-yellow-200 rounded-sm" style={{ height: '50%' }}>
            <div className="h-full flex items-end justify-center pb-0.5">
              <span className="text-[10px] text-yellow-800">61</span>
            </div>
          </div>
          <div className="w-1/6 bg-yellow-300 rounded-sm" style={{ height: '60%' }}>
            <div className="h-full flex items-end justify-center pb-0.5">
              <span className="text-[10px] text-yellow-800">68</span>
            </div>
          </div>
          <div className="w-1/6 bg-teal-300 rounded-sm" style={{ height: '70%' }}>
            <div className="h-full flex items-end justify-center pb-0.5">
              <span className="text-[10px] text-teal-800">76</span>
            </div>
          </div>
          <div className="w-1/6 bg-teal-400 rounded-sm" style={{ height: '80%' }}>
            <div className="h-full flex items-end justify-center pb-0.5">
              <span className="text-[10px] text-teal-800">82</span>
            </div>
          </div>
          <div className="w-1/6 bg-green-500 rounded-sm" style={{ height: '90%' }}>
            <div className="h-full flex items-end justify-center pb-0.5">
              <span className="text-[10px] text-white">91</span>
            </div>
          </div>
        </div>
        
        <div className="flex text-[9px] text-gray-500 justify-between px-1">
          <span>Initial</span>
          <span>Current</span>
        </div>
        
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div className="ml-2">
              <span className="text-xs font-medium text-green-700">91/100</span>
              <span className="text-xs text-gray-500 ml-1">- Ready for approval</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stakeholder Collaboration */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-700 mb-3">Stakeholder Updates</h5>
        
        <div className="space-y-3">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center">
              <span className="text-xs font-medium text-blue-700">JS</span>
            </div>
            <div className="ml-3 flex-1">
              <div className="flex justify-between">
                <span className="text-xs font-medium text-gray-800">John Smith - Adjuster</span>
                <span className="text-[10px] text-gray-500">15m ago</span>
              </div>
              <p className="text-xs text-gray-600">Added final inspection report with updated photos</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex-shrink-0 flex items-center justify-center">
              <span className="text-xs font-medium text-purple-700">MT</span>
            </div>
            <div className="ml-3 flex-1">
              <div className="flex justify-between">
                <span className="text-xs font-medium text-gray-800">Maria Torres - Legal</span>
                <span className="text-[10px] text-gray-500">1h ago</span>
              </div>
              <p className="text-xs text-gray-600">Reviewed policy terms and approved claim amount</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center">
              <span className="text-xs font-medium text-green-700">AJ</span>
            </div>
            <div className="ml-3 flex-1">
              <div className="flex justify-between">
                <span className="text-xs font-medium text-gray-800">Amy Johnson - Owner</span>
                <span className="text-[10px] text-gray-500">3h ago</span>
              </div>
              <p className="text-xs text-gray-600">Submitted final repair estimate from contractor</p>
            </div>
          </div>
        </div>
        
        <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200">
          <div className="flex items-center">
            <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center">
              <Brain className="w-3.5 h-3.5 text-teal-600" />
            </div>
            <div className="ml-2 flex-1">
              <span className="text-xs font-medium text-gray-800">AI Assistant</span>
              <p className="text-xs text-gray-600">All required documentation received. Final approval recommended. Risk score: 91/100</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
